<template>
  <BaseModal
    :modalTitle="$t('W: Kunderapport — Forhåndsvisning')"
    contentPadding="none">
    <div
      v-if="isLoading"
      class="flex items-center justify-center gap-2 px-6 py-10 text-sm text-text-muted">
      <IconLucideLoader2 class="h-4 w-4 animate-spin" />
      <span>{{ $t('W: Bygger forhåndsvisningen') }}</span>
    </div>

    <p
      v-else-if="errorText"
      class="px-6 py-10 text-center text-sm text-feedback-error"
      data-testid="customer-report-preview-error">
      {{ errorText }}
    </p>

    <template v-else>
      <!-- The same bar as the lab report preview: the PDF as it would be generated now, saved as a draft. -->
      <div class="flex items-center justify-end gap-3 border-b border-border-subtle px-6 py-3">
        <span
          v-if="pdfError"
          class="text-feedback-error text-sm"
          data-testid="customer-report-preview-pdf-error">
          {{ pdfError }}
        </span>
        <UiBaseButton
          :text="$t('W: Download PDF')"
          :isLoading="isGeneratingPdf"
          data-testid="customer-report-preview-download"
          @click="generatePdf">
          <template #icon-left>
            <IconLucideDownload />
          </template>
        </UiBaseButton>
      </div>

      <!-- Same frame as the lab report preview: the A4 page at 80 %, height taken from the content. -->
      <div class="flex justify-center bg-surface-sunken px-6 pt-4 pb-6">
        <div
          ref="previewContainer"
          class="overflow-hidden"
          style="width: 684px">
          <iframe
            ref="previewIframe"
            :srcdoc="previewHtml"
            scrolling="no"
            style="
              width: 854px;
              height: 100px;
              transform: scale(0.8);
              transform-origin: top left;
              border: 0;
              display: block;
            "
            :title="$t('W: Forhåndsvisning af kunderapport')"
            data-testid="customer-report-preview-frame"
            @load="onIframeLoad" />
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import { useAccountStore } from '@/stores/accountStore'
import { getCurrentUserBearer } from '@/globals/typescript/utils/api/getCurrentUserBearer'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { HOST } from '@/globals/typescript/utils/host'
import { reportUnexpectedError } from '@/globals/typescript/utils/reportError'
import { downloadBlob } from '@/globals/typescript/utils/downloadBlob'
import { useToast } from '@/globals/typescript/utils/toast'

/**
 * Previews the order's customer report: the same HTML the PDF is made from, built on the server
 * from the order's current state and shown the way the lab report preview shows its own, with the
 * same "Download PDF" — the preview as a PDF, saved as a draft so it cannot be mistaken for the
 * report a customer generated.
 */
export default {
  name: 'OrderCustomerReportPreviewModal',

  props: {
    orderID: { type: String, required: true },
  },

  data() {
    return {
      isLoading: true,
      errorText: '',
      previewHtml: '',
      /** What a downloaded preview is saved as — the report's name tagged as a draft, from the server. */
      fileName: '',
      isGeneratingPdf: false,
      pdfError: '',
    }
  },

  computed: {
    ...mapState(useAccountStore, ['labAccount']),
  },

  methods: {
    async load(): Promise<void> {
      const labAccountID = this.labAccount?.id
      if (!labAccountID) {
        // Nothing to load without a lab — do not leave the spinner up for good.
        this.isLoading = false
        return
      }

      try {
        const result = await managementApi(
          'get',
          'lab-accounts/{labAccountID}/orders/{orderID}/customer-report-preview',
          { path: { labAccountID, orderID: this.orderID } }
        )
        if (!result.success) {
          this.fail()
          return
        }
        this.previewHtml = result.data.html
        this.fileName = result.data.fileName
      } catch (error) {
        reportUnexpectedError(error, {
          operation: 'customer-report-preview',
          extra: { orderID: this.orderID },
        })
        this.fail()
      } finally {
        this.isLoading = false
      }
    },

    fail(): void {
      this.errorText = this.$t('W: Kunne ikke bygge forhåndsvisningen af kunderapporten')
      useToast().error(this.errorText)
    },

    /**
     * The preview as a PDF. The server builds the HTML again from the same code and renders it, so
     * the browser sends nothing but the order; the file is saved under the draft name it gave.
     */
    async generatePdf(): Promise<void> {
      const labAccountID = this.labAccount?.id ?? ''
      if (!labAccountID) return

      this.isGeneratingPdf = true
      this.pdfError = ''
      try {
        let response: Response
        try {
          const token = await getCurrentUserBearer()
          response = await fetch(
            `${HOST.functions}/management/v1/lab-accounts/${labAccountID}/orders/${this.orderID}/customer-report-preview/pdf`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        } catch (error) {
          // The request never reached lab-cloud, so nobody else has seen this.
          reportUnexpectedError(error, {
            operation: 'customer-report-preview-pdf',
            extra: { orderID: this.orderID },
          })
          this.failPdf()
          return
        }
        // A refusal or a rendering failure was already answered — and, if unexpected, reported — by lab-cloud.
        if (!response.ok) {
          this.failPdf()
          return
        }

        downloadBlob(await response.blob(), this.fileName || 'Kunderapport.pdf')
        useToast().success(this.$t('W: PDF downloadet'))
      } finally {
        this.isGeneratingPdf = false
      }
    },

    failPdf(): void {
      this.pdfError = this.$t('W: PDF kunne ikke genereres')
      useToast().error(this.$t('W: PDF kunne ikke genereres'))
    },

    onIframeLoad(): void {
      const iframe = this.$refs.previewIframe as HTMLIFrameElement | undefined
      if (!iframe?.contentWindow?.document?.body) return
      const scrollHeight = iframe.contentWindow.document.body.scrollHeight
      iframe.style.height = scrollHeight + 'px'
      const container = this.$refs.previewContainer as HTMLElement | undefined
      if (container) {
        container.style.height = Math.round(scrollHeight * 0.8) + 'px'
      }
    },
  },

  created() {
    void this.load()
  },
}
</script>
